import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  MapPin, Navigation, Phone, User, Clock, Car,
  CheckCircle, ArrowRight, Loader2, RefreshCw
} from 'lucide-react';

const STATUS_CONFIG = {
  offline: { label: 'Offline', color: 'bg-gray-500', icon: Clock },
  available: { label: 'Available', color: 'bg-green-500', icon: CheckCircle },
  en_route: { label: 'En Route', color: 'bg-blue-500', icon: Navigation },
  arrived: { label: 'Arrived', color: 'bg-purple-500', icon: MapPin },
  trip_started: { label: 'Trip Started', color: 'bg-orange-500', icon: Car },
  completed: { label: 'Completed', color: 'bg-green-600', icon: CheckCircle }
};

// Driver Status Update Component (for drivers)
export const DriverStatusUpdater = ({ bookingId, currentStatus, onStatusUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(currentStatus || 'available');

  const statusFlow = ['en_route', 'arrived', 'trip_started', 'completed'];

  const updateStatus = async (newStatus) => {
    setLoading(true);
    try {
      await api.post('/driver/status', {
        booking_id: bookingId,
        status: newStatus
      });
      setStatus(newStatus);
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label}`);
      if (onStatusUpdate) onStatusUpdate(newStatus);
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getNextStatus = () => {
    const currentIndex = statusFlow.indexOf(status);
    if (currentIndex < statusFlow.length - 1) {
      return statusFlow[currentIndex + 1];
    }
    return null;
  };

  const nextStatus = getNextStatus();

  return (
    <Card className="bg-secondary/30 border-white/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Car className="h-4 w-4 text-primary" />
          Trip Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Status Progress */}
        <div className="flex items-center justify-between mb-4">
          {statusFlow.map((s, index) => {
            const config = STATUS_CONFIG[s];
            const isActive = statusFlow.indexOf(status) >= index;
            const Icon = config.icon;
            
            return (
              <React.Fragment key={s}>
                <div className={`flex flex-col items-center ${isActive ? 'opacity-100' : 'opacity-40'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isActive ? config.color : 'bg-gray-600'}`}>
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-xs mt-1 text-muted-foreground">{config.label}</span>
                </div>
                {index < statusFlow.length - 1 && (
                  <ArrowRight className={`h-4 w-4 ${isActive ? 'text-primary' : 'text-gray-600'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Current Status Badge */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-muted-foreground">Current Status:</span>
          <Badge className={STATUS_CONFIG[status]?.color}>
            {STATUS_CONFIG[status]?.label}
          </Badge>
        </div>

        {/* Next Status Button */}
        {nextStatus && status !== 'completed' && (
          <Button
            onClick={() => updateStatus(nextStatus)}
            disabled={loading}
            className="w-full"
            data-testid="update-status-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              nextStatus && STATUS_CONFIG[nextStatus]?.icon ? 
                React.createElement(STATUS_CONFIG[nextStatus].icon, { className: "h-4 w-4 mr-2" }) : null
            )}
            Mark as {STATUS_CONFIG[nextStatus]?.label}
          </Button>
        )}

        {status === 'completed' && (
          <div className="text-center text-green-500 py-2">
            <CheckCircle className="h-8 w-8 mx-auto mb-2" />
            Trip Completed
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Driver Location Tracker (for clients to view driver location)
export const DriverLocationTracker = ({ bookingId }) => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLocation = useCallback(async () => {
    try {
      const response = await api.get(`/booking/${bookingId}/driver-location`);
      setLocation(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Driver location not available yet');
      } else {
        setError('Failed to fetch driver location');
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchLocation();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchLocation, 10000);
    return () => clearInterval(interval);
  }, [fetchLocation]);

  if (loading) {
    return (
      <Card className="bg-secondary/30 border-white/10">
        <CardContent className="py-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground mt-2">Loading driver location...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-secondary/30 border-white/10">
        <CardContent className="py-6 text-center">
          <MapPin className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          <Button variant="ghost" size="sm" onClick={fetchLocation} className="mt-2">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const statusConfig = STATUS_CONFIG[location?.driver_status] || STATUS_CONFIG.available;

  return (
    <Card className="bg-secondary/30 border-white/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Navigation className="h-4 w-4 text-primary" />
            Driver Tracking
          </span>
          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Driver Info */}
        <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-medium">{location.driver_name}</p>
            {location.driver_phone && (
              <a 
                href={`tel:${location.driver_phone}`}
                className="text-sm text-primary flex items-center gap-1"
              >
                <Phone className="h-3 w-3" />
                {location.driver_phone}
              </a>
            )}
          </div>
        </div>

        {/* Location Details */}
        {location.latitude && location.longitude && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Location:</span>
              <span>{location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</span>
            </div>
            {location.speed !== null && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Speed:</span>
                <span>{Math.round(location.speed)} km/h</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Last Updated:</span>
              <span>{new Date(location.last_updated).toLocaleTimeString()}</span>
            </div>
          </div>
        )}

        {/* Map Link */}
        {location.latitude && location.longitude && (
          <a
            href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button variant="outline" className="w-full">
              <MapPin className="h-4 w-4 mr-2" />
              View on Google Maps
            </Button>
          </a>
        )}
      </CardContent>
    </Card>
  );
};

// GPS Location Sharing Component (for drivers)
export const DriverGPSSharing = ({ bookingId, onLocationUpdate }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);

  const startSharing = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    const id = navigator.geolocation.watchPosition(
      async (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          heading: position.coords.heading,
          speed: position.coords.speed ? position.coords.speed * 3.6 : null, // Convert m/s to km/h
          accuracy: position.coords.accuracy
        };

        setCurrentLocation(locationData);

        try {
          await api.post('/driver/location', locationData, {
            params: { booking_id: bookingId }
          });
          if (onLocationUpdate) onLocationUpdate(locationData);
        } catch (error) {
          console.error('Failed to update location:', error);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Failed to get your location');
        setIsSharing(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 5000
      }
    );

    setWatchId(id);
    setIsSharing(true);
    toast.success('Location sharing started');
  };

  const stopSharing = () => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    setIsSharing(false);
    toast.info('Location sharing stopped');
  };

  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  return (
    <Card className="bg-secondary/30 border-white/10">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Navigation className="h-4 w-4 text-primary" />
          GPS Location Sharing
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Share your real-time location with the passenger and dispatch.
        </p>

        {currentLocation && isSharing && (
          <div className="text-sm space-y-1 p-3 bg-background/50 rounded-lg">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lat:</span>
              <span>{currentLocation.latitude.toFixed(6)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lng:</span>
              <span>{currentLocation.longitude.toFixed(6)}</span>
            </div>
            {currentLocation.speed !== null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Speed:</span>
                <span>{Math.round(currentLocation.speed)} km/h</span>
              </div>
            )}
          </div>
        )}

        <Button
          onClick={isSharing ? stopSharing : startSharing}
          variant={isSharing ? "destructive" : "default"}
          className="w-full"
          data-testid="gps-toggle-btn"
        >
          {isSharing ? (
            <>
              <Navigation className="h-4 w-4 mr-2 animate-pulse" />
              Stop Sharing Location
            </>
          ) : (
            <>
              <Navigation className="h-4 w-4 mr-2" />
              Start Sharing Location
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DriverLocationTracker;
